import { FC, useEffect, useMemo } from 'react';
import {
  WbButton,
  WbDockableGroup,
  WbDockablePanel,
  WbDockableResizeHandle,
  WbTabsProvider,
} from '@agilelab/plugin-wb-platform';
import { TeamRolesCard } from './TeamRolesCard';
import { TeamRoleAssignmentsCard } from './TeamRoleAssignmentsCard';
import { teamRolesNavigatedTopic } from '@agilelab/plugin-wb-events-common';
import { useTeamRolesPanelContext } from '../../context';
import { usePublishEvent } from '@agilelab/plugin-wb-events';
import { stringifyEntityRef } from '@mainstage/catalog-model';
import { TeamRoleEnum } from '@agilelab/plugin-wb-team-roles-common';
import { Paper } from '@material-ui/core';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import {
  WB_HEADER_HEIGHT,
  WB_HEADER_BREADCRUMBS_HEIGHT,
} from '@agilelab/plugin-wb-platform';

export const TeamRolesPanel: FC = () => {
  const {
    project,
    projectUrn,
    selectedTeamRole,
    hasTroubleshootAccess,
    isOwner,
    isLimitedOwner,
    enabledTeamRoles,
    assignments,
    setOpenCreateTeamRoleAssignmentDrawer,
  } = useTeamRolesPanelContext();
  const { publish } = usePublishEvent({
    generatorSystem: 'builder',
    keyBuilderCatalog: stringifyEntityRef(project),
  });

  useEffect(() => {
    publish(teamRolesNavigatedTopic, {
      entityUrn: projectUrn,
    });
  }, [projectUrn, publish]);

  const canCreateAssignment = useMemo(
    () =>
      assignments &&
      assignments.length > 0 &&
      (isOwner ||
        hasTroubleshootAccess ||
        (selectedTeamRole !== TeamRoleEnum.OWNER && isLimitedOwner)),
    [
      assignments,
      isOwner,
      isLimitedOwner,
      hasTroubleshootAccess,
      selectedTeamRole,
    ],
  );

  // Header (breadcrumbs + title) + page tabs + content margin
  const panelHeight = `calc(100vh - ${
    WB_HEADER_HEIGHT + WB_HEADER_BREADCRUMBS_HEIGHT
  }px - 68px)`;

  const currentTeamRole = useMemo(
    () => enabledTeamRoles.find(teamRole => teamRole.name === selectedTeamRole),
    [enabledTeamRoles, selectedTeamRole],
  );

  return (
    <WbTabsProvider>
      <Paper style={{ height: panelHeight }}>
        <WbDockableGroup direction="horizontal">
          <WbDockablePanel
            defaultSize={15}
            maxSize={25}
            title="Team Roles"
            headerBorderBottom
          >
            <TeamRolesCard />
          </WbDockablePanel>

          <WbDockableResizeHandle direction="horizontal" />

          <WbDockablePanel
            title={`${currentTeamRole?.displayName} Assignments`}
            headerBorderBottom
            headerActions={
              canCreateAssignment ? (
                <WbButton
                  label="Add Assignment"
                  startIcon={<AddCircleIcon />}
                  color="primary"
                  onClick={() => {
                    setOpenCreateTeamRoleAssignmentDrawer(true);
                  }}
                />
              ) : undefined
            }
          >
            <TeamRoleAssignmentsCard />
          </WbDockablePanel>
        </WbDockableGroup>
      </Paper>
    </WbTabsProvider>
  );
};
